import { Drawer, WidgetSpinner } from '@/components'
import { TotalEmissionsReportSettings } from '@/hooks'
import { GetSustainabilityTotalEmissionsRequestParams } from '@/hooks'
import { Scope2TypeSelect } from '@/features/Sustainability/components/TotalEmissionsDrawer/Scope2TypeSelect'
import { Dispatch, SetStateAction } from 'react'
import { useTranslation } from 'react-i18next'
import { DisplaySettings } from './DisplaySettings'
import { EmissionScopeSelect } from './EmissionScopeSelect'
import { DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS } from '@/utils/constants'
import { ResetReportSettingsButton } from '@/components/molecules/ResetReportSettingsButton/ResetReportSettingsButton'
import { SettingsTextCard } from '@/components/molecules/SettingsTextCard/SettingsTextCard'
import useSustainabilityStore from '../../stores/useSustainabilityStore/useSustainabilityStore'

export type TotalEmissionsDrawerProps = {
    reportSettings: TotalEmissionsReportSettings
    setReportSettings: (settings: Partial<TotalEmissionsReportSettings>) => void
    isOpen: boolean
    setIsOpen: Dispatch<SetStateAction<boolean>>
    toggleButtonRef?: React.Ref<HTMLButtonElement>
    showResetReportSettingsButton?: boolean
    filters: GetSustainabilityTotalEmissionsRequestParams
    isMarketBasedScope2Available?: boolean
    isDataReady?: boolean
}

export const TotalEmissionsDrawer: React.FC<TotalEmissionsDrawerProps> = (
    props
) => {
    const {
        reportSettings,
        setReportSettings,
        isOpen,
        setIsOpen,
        toggleButtonRef,
        showResetReportSettingsButton,
        filters,
        isDataReady,
        isMarketBasedScope2Available = true,
    } = props

    const { t } = useTranslation('translations')

    const hasDefaultSettings = useSustainabilityStore(
        (state) => state.totalEmissions.hasDefaultSettings
    )

    const isResetHidden =
        showResetReportSettingsButton !== undefined
            ? !showResetReportSettingsButton
            : hasDefaultSettings

    return (
        <Drawer
            drawerToggleLabel={t('sustainability.report_settings.title')}
            drawerToggleIconName="tune"
            title={t('sustainability.report_settings.title')}
            isOpen={isOpen}
            setIsOpen={setIsOpen}
            toggleButtonRef={toggleButtonRef}
            headerContent={
                <ResetReportSettingsButton
                    onClick={() => {
                        setReportSettings(
                            DEFAULT_TOTAL_EMISSIONS_REPORT_SETTINGS
                        )
                    }}
                    isHidden={isResetHidden}
                    data-testid={'reset-settings-button'}
                />
            }
        >
            {isDataReady ? (
                <div className="space-y-7">
                    <div className="space-y-3">
                        <div className="text-heading-caps">
                            {t('sustainability.report_settings.setup')}
                        </div>
                        <DisplaySettings
                            reportSettings={reportSettings}
                            onChange={(type) => {
                                setReportSettings({ type })
                            }}
                            filters={filters}
                        />
                    </div>
                    <div className="space-y-3">
                        <div className="text-heading-caps">
                            {t(
                                'sustainability.report_settings.operational_emissions'
                            )}
                        </div>
                        <EmissionScopeSelect
                            value={reportSettings.scope}
                            onChange={(scope) => {
                                setReportSettings({ scope })
                            }}
                        />
                        {reportSettings.scope !== 'scope2' && (
                            <SettingsTextCard
                                title={t(
                                    'sustainability.report_settings.scope1Type.title'
                                )}
                                description={t(
                                    'sustainability.report_settings.scope1Type.description'
                                )}
                            />
                        )}
                        {reportSettings.scope !== 'scope1' &&
                            reportSettings.scope2type && (
                                <Scope2TypeSelect
                                    isMarketBasedScope2Available={
                                        isMarketBasedScope2Available
                                    }
                                    value={reportSettings.scope2type}
                                    onChange={(scope2type) => {
                                        setReportSettings({
                                            scope2type,
                                        })
                                    }}
                                />
                            )}
                    </div>
                </div>
            ) : (
                <div className="flex h-full items-center justify-center">
                    <WidgetSpinner />
                </div>
            )}
        </Drawer>
    )
}
